import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { EmptyState } from '../ui/EmptyState';
import { InAppNotification } from '@/types/intelligentScheduling';
import { smartNotificationService } from '@/services/intelligentScheduling/SmartNotificationService';
import { 
  History as HistoryIcon, 
  Search, 
  Filter, 
  Clock, 
  Bell, 
  AlertCircle, 
  Target, 
  Calendar,
  Trash2,
  BarChart3
} from 'lucide-react';

interface NotificationHistoryStats {
  total: number;
  unread: number;
  byType: { [key: string]: number };
  byPriority: { [key: string]: number };
  averageResponseTime: number;
  topActions: { action: string; count: number }[];
}

export function NotificationHistory() {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<InAppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [stats, setStats] = useState<NotificationHistoryStats>({
    total: 0,
    unread: 0,
    byType: {},
    byPriority: {},
    averageResponseTime: 0,
    topActions: []
  });

  useEffect(() => {
    loadNotificationHistory();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [notifications, searchQuery, filterType, filterPriority]);

  const loadNotificationHistory = async () => {
    try {
      setIsLoading(true);
      
      // Load from localStorage (in real app, this would be from API)
      const stored = localStorage.getItem('godmode_notifications');
      const historyData: InAppNotification[] = stored ? JSON.parse(stored) : [];
      
      // Also get current active notifications
      const activeNotifications = smartNotificationService.getActiveNotifications();
      
      // Combine and sort by creation date
      const allNotifications = [...historyData, ...activeNotifications]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // Remove duplicates based on ID
      const uniqueNotifications = allNotifications.filter(
        (notification, index, array) => 
          array.findIndex(n => n.id === notification.id) === index
      );
      
      setNotifications(uniqueNotifications);
      calculateStats(uniqueNotifications);
    } catch (error) {
      console.error('Failed to load notification history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (notifications: InAppNotification[]) => {
    const stats: NotificationHistoryStats = {
      total: notifications.length,
      unread: notifications.filter(n => !n.isRead).length,
      byType: {},
      byPriority: {},
      averageResponseTime: 0,
      topActions: []
    };

    // Calculate type distribution
    notifications.forEach(notification => {
      stats.byType[notification.type] = (stats.byType[notification.type] || 0) + 1;
      stats.byPriority[notification.priority] = (stats.byPriority[notification.priority] || 0) + 1;
    });

    // Calculate top actions (mock data for now)
    stats.topActions = [
      { action: 'start', count: Math.floor(notifications.length * 0.4) },
      { action: 'snooze', count: Math.floor(notifications.length * 0.3) },
      { action: 'dismiss', count: Math.floor(notifications.length * 0.2) },
      { action: 'reschedule', count: Math.floor(notifications.length * 0.1) }
    ];

    setStats(stats);
  };

  const applyFilters = () => {
    let filtered = notifications;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(notification =>
        notification.title.toLowerCase().includes(query) ||
        notification.message.toLowerCase().includes(query) ||
        notification.type.toLowerCase().includes(query)
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(notification => notification.type === filterType);
    }

    // Priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(notification => notification.priority === filterPriority);
    }

    setFilteredNotifications(filtered);
  };

  const clearHistory = () => {
    if (window.confirm('Are you sure you want to clear all notification history? This cannot be undone.')) {
      localStorage.removeItem('godmode_notifications');
      setNotifications([]);
      setFilteredNotifications([]);
      calculateStats([]);
    }
  };

  const getIconForNotificationType = (type: string) => {
    switch (type) {
      case 'schedule_reminder':
        return <Clock className="h-4 w-4" />;
      case 'deadline_warning':
        return <AlertCircle className="h-4 w-4" />;
      case 'task_suggestion':
        return <Target className="h-4 w-4" />;
      case 'break_reminder':
        return <Calendar className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20';
      case 'high':
        return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20';
      case 'medium':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diffInHours = (now.getTime() - d.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return d.toLocaleDateString();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Bell className="h-4 w-4 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <div>
                <p className="text-2xl font-bold">{stats.unread}</p>
                <p className="text-xs text-muted-foreground">Unread</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Target className="h-4 w-4 text-green-600" />
              <div>
                <p className="text-2xl font-bold">
                  {stats.topActions[0]?.count || 0}
                </p>
                <p className="text-xs text-muted-foreground">Actions taken</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">
                  {Math.round((stats.total - stats.unread) / Math.max(stats.total, 1) * 100)}%
                </p>
                <p className="text-xs text-muted-foreground">Read rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select
              value={filterType}
              onValueChange={setFilterType}
            >
              <option value="all">All types</option>
              <option value="schedule_reminder">Schedule reminders</option>
              <option value="deadline_warning">Deadline warnings</option>
              <option value="task_suggestion">Task suggestions</option>
              <option value="break_reminder">Break reminders</option>
            </Select>

            <Select
              value={filterPriority}
              onValueChange={setFilterPriority}
            >
              <option value="all">All priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
          </div>

          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredNotifications.length} of {notifications.length} notifications
            </p>
            
            <Button
              variant="outline"
              size="sm"
              onClick={clearHistory}
              disabled={notifications.length === 0}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear History
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <HistoryIcon className="h-5 w-5 mr-2" />
            Notification History
          </CardTitle>
          <CardDescription>
            Your complete notification history with details and analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredNotifications.length === 0 ? (
            <EmptyState
              icon={<HistoryIcon className="h-12 w-12" />}
              title="No notifications found"
              description={
                searchQuery || filterType !== 'all' || filterPriority !== 'all'
                  ? "No notifications match your current filters. Try adjusting your search or filters."
                  : "You haven't received any notifications yet. They'll appear here once you start getting reminders and updates."
              }
            />
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border transition-all hover:shadow-sm ${
                    !notification.isRead 
                      ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800' 
                      : 'bg-background dark:bg-gray-950 border-gray-200 dark:border-gray-800'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-full flex-shrink-0 ${getPriorityColor(notification.priority)}`}>
                      {getIconForNotificationType(notification.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className={`text-sm font-medium truncate ${
                          !notification.isRead ? 'text-foreground' : 'text-muted-foreground'
                        }`}>
                          {notification.title}
                        </h3>
                        <div className="flex items-center space-x-2 ml-2">
                          <Badge
                            variant={notification.priority === 'critical' || notification.priority === 'high' ? 'destructive' : 'secondary'}
                            className="text-xs"
                          >
                            {notification.priority}
                          </Badge>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {notification.message}
                      </p>

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatDate(notification.createdAt)}
                          </span>
                          <span className="capitalize">
                            {notification.type.replace('_', ' ')}
                          </span>
                          {notification.confidence && (
                            <span>
                              {Math.round(notification.confidence * 100)}% confidence
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Contextual factors */}
                      {notification.contextualFactors.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {notification.contextualFactors.slice(0, 3).map(factor => (
                            <Badge key={factor} variant="outline" className="text-xs">
                              {factor.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}